<template>
  <PageLayout :title="$t('recurring.title')" :subtitle="$t('recurring.subtitle')">
    <template #actions>
      <router-link to="/posts/create?recurring=1" class="btn btn-primary">
        <i class="fas fa-plus mr-1"></i> {{ $t('recurring.create') }}
      </router-link>
    </template>

    <ContentCard>
      <div v-if="loading" class="text-muted">{{ $t('common.loading') }}</div>
      <div v-else-if="items.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-redo fa-2x mb-3 d-block"></i>
        <p class="mb-3">{{ $t('recurring.empty') }}</p>
        <router-link to="/posts/create?recurring=1" class="btn btn-outline-primary btn-sm">
          {{ $t('recurring.create_from_composer') }}
        </router-link>
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>{{ $t('common.name') }}</th>
              <th>{{ $t('global.account') }}</th>
              <th>{{ $t('global.type') }}</th>
              <th>{{ $t('recurring.schedule') }}</th>
              <th>{{ $t('recurring.next_run') }}</th>
              <th>{{ $t('global.status') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>
                <div class="font-weight-bold">{{ item.name }}</div>
                <small class="text-muted text-truncate d-inline-block" style="max-width: 220px">
                  {{ item.caption || '-' }}
                </small>
              </td>
              <td>@{{ item.social_account?.username }}</td>
              <td><span class="badge badge-light text-capitalize">{{ item.type }}</span></td>
              <td>{{ scheduleLabel(item) }}</td>
              <td>{{ formatDate(item.next_run_at) }}</td>
              <td>
                <span class="badge" :class="item.status === 'active' ? 'badge-success' : 'badge-secondary'">
                  {{ statusLabel(item.status) }}
                </span>
              </td>
              <td class="text-nowrap">
                <button
                  v-if="item.status === 'active'"
                  class="btn btn-xs btn-outline-secondary mr-1"
                  @click="onToggle(item, 'paused')"
                >
                  {{ $t('common.pause') }}
                </button>
                <button
                  v-else
                  class="btn btn-xs btn-outline-success mr-1"
                  @click="onToggle(item, 'active')"
                >
                  {{ $t('common.resume') }}
                </button>
                <button class="btn btn-xs btn-outline-danger" @click="onDelete(item.id)">{{ $t('common.delete') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  computed: {
    ...mapGetters('recurring', ['items', 'loading']),
  },
  async created() {
    await this.fetchItems()
  },
  methods: {
    ...mapActions('recurring', ['fetchItems', 'updateItem', 'deleteItem']),
    scheduleLabel(item) {
      if (item.frequency === 'weekly') {
        const day = this.weekdayOptions().find((d) => d.value === item.day_of_week)?.label
        return this.$t('recurring.weekly', { day, time: item.time_of_day })
      }
      if (item.frequency === 'monthly') {
        return this.$t('recurring.monthly', { day: item.day_of_month, time: item.time_of_day })
      }
      return this.$t('recurring.daily', { time: item.time_of_day })
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleString() : '-'
    },
    async onToggle(item, status) {
      await this.updateItem({ id: item.id, payload: { status } })
      await this.fetchItems()
    },
    async onDelete(id) {
      const ok = await this.$confirm(this.$t('recurring.delete_confirm'), {
        confirmText: this.$t('common.delete'),
        danger: true,
      })
      if (!ok) return
      await this.deleteItem(id)
      await this.fetchItems()
    },
  },
}
</script>
